/**
 * VIVANA × IT GEEKS — Subscription Cart Configuration
 *
 * Loaded with `defer` from theme.liquid (P02). Because inline <script> blocks
 * in theme.liquid run DURING HTML parse (before any deferred external script),
 * we no longer own `window.VIVANA_SUBSCRIPTION_CONFIG` at load time — inline
 * scripts may have already put values on it (`maxQuantity`, `contactEmail`,
 * `subscriptionBundleSwap.bundleMap`, etc.). This module therefore does a
 * defensive per-key merge instead of overwriting the object, so nothing set
 * inline is clobbered.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MAINTENANCE
 *  - Appstle Selling Plan IDs:  Appstle Admin → Subscription Plans → copy Plan ID
 *  - DOI Pack (Refill Doypack) Variant IDs:  Shopify Admin → Products → Variants
 *
 * The implementation works WITHOUT filling these maps (it falls back to
 * classifying plans by their name and resolving the doypack variant by title),
 * but populating them makes behaviour deterministic and is recommended for
 * production. See PRODUCTION IMPLEMENTATION CHECKLIST in the technical doc.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  /** @type {*} */
  var defaults = {
    /**
     * Explicit Selling Plan classification.
     * Format: { [sellingPlanId]: 'monthly' | 'quarterly' | 'annual' }
     *
     * Appstle creates one selling plan per product per tier, so IDs differ per
     * product — add every Quarterly and Annual plan ID here. Monthly plans do NOT
     * trigger DOI packs, so they are optional to list.
     */
    sellingPlanTierMap: {
      // Main products (Balance+, The Foundation – The Basics) share the same
      // Appstle "Group A" selling plans.
      '724279722315': 'monthly',
      '724279787851': 'quarterly',
      '724279820619': 'annual',
      // Subscription / bundle products share the "Group A Quarterly" plans.
      // (These are read live by the handle-based resolver too — listed here so
      // classification stays deterministic if Appstle plan names ever change.)
      '724329791819': 'monthly',
      '724329824587': 'quarterly',
      '724329857355': 'annual',
    },

    /**
     * Fallback tier classification by selling-plan NAME (case-insensitive
     * substring match). Used only when a plan ID is not found in
     * sellingPlanTierMap. Order matters — first match wins.
     */
    tierNameKeywords: [
      { tier: 'annual', keywords: ['annual', 'yearly', 'year', '25%'] },
      { tier: 'quarterly', keywords: ['quarter', '3 month', 'every 3', '3-month', '20%'] },
      { tier: 'monthly', keywords: ['month', '15%'] },
    ],

    /**
     * DOI Pack (Refill Doypack) quantity added on the FIRST order per tier.
     * Multiplied by the main subscription quantity.
     *   monthly   → 0 (no extra doypacks)
     *   quarterly → 2 (jar covers month 1, doypacks cover months 2 & 3)
     *   annual    → 2 (jar covers month 1, doypacks cover months 2 & 3 of first wave)
     */
    doiPackQuantityMap: {
      monthly: 0,
      quarterly: 2,
      annual: 2,
    },

    /**
     * Product → DOI Pack Variant ID mapping.
     * Format: { [productId]: doiPackVariantId }
     * Optional — if absent, the doypack variant is resolved from a
     * `data-vivana-doi-variant-id` form attribute, then by variant title.
     */
    productDoiVariantMap: {
      // 1234567890: 9876543210,
    },

    /**
     * Line item property keys used to tag and track DOI pack line items.
     * Underscore-prefixed keys are hidden from customer-facing order pages.
     */
    properties: {
      isDoiPack: '_doi_pack',
      linkedVariant: '_linked_variant',
      doiPlan: '_doi_plan',
      customerLabel: 'Included with subscription', // visible in Admin / emails
      bundleSwap: '_sub_bundle', // marks a line substituted in by the bundle swap
      bundleSource: '_sub_bundle_source', // the main variant id it replaced
      bundleSourceUrl: '_sub_bundle_url', // main product URL (cart links here, not the hidden page)
      packLabel: 'Pack', // VISIBLE cart label for one-time packs (e.g. "Three-Month Pack")
      packMode: '_pack_mode', // hidden: 'three' | 'six' — drives the cart step-by-2 for Six-Month
    },

    /** Customer-visible value for the `Included with subscription` property. */
    customerLabelValue: 'Yes',

    /**
     * Store-wide maximum quantity cap. Overridden at runtime by the theme setting
     * `max_subscription_quantity` (injected in theme.liquid). Kept here as a safe
     * default so the module still works if the inline injection is ever removed.
     */
    maxQuantity: 10,

    /** Contact email shown when the cap is reached (overridden by theme setting). */
    contactEmail: 'info@vivanalife.com',

    /**
     * Case-insensitive substrings that identify a DOI pack / refill VARIANT by its
     * option-value name. Used to hide it from purchase UI (Liquid) and to resolve
     * it in JS. App-independent and zero-setup. (REQ 1 / REQ 7 portability.)
     */
    doiVariantNameKeywords: ['doypack', 'refill'],

    /**
     * SUBSCRIPTION BUNDLE SWAP (context.md §F).
     * When a customer picks Quarterly/Annual on a MAIN product, swap the cart line
     * for a hidden BUNDLE product so the storefront/cart/checkout/order show only
     * one bundle line. One-time and Monthly are left untouched.
     *
     * ── DYNAMIC HANDLE-BASED MATCHING ──────────────────────────────────────────
     * The bundle is found from the MAIN product's HANDLE, not its name or a manual
     * pairing: the subscription product is expected to live at
     *     `${mainHandle}${handleSuffix}`   e.g.  balance → balance-subscription
     *                                            the-foundation-the-basics
     *                                              → the-foundation-the-basics-subscription
     * The resolver fetches `/products/<that-handle>.js` and reads the bundle's
     * variant id and its per-tier selling-plan ids LIVE, so there is no hardcoded
     * variant/plan mapping to maintain. Any future product works automatically as
     * long as its subscription product follows this handle convention.
     *
     * Tier is classified from the MAIN product's selected selling plan by the same
     * PlanClassifier used for DOI packs; only DOI-bearing tiers (quarterly/annual)
     * swap — one-time and monthly stay on the main product.
     */
    subscriptionBundleSwap: {
      enabled: true,
      // When true, rewrite the /cart/add request in-flight so the bundle is added
      // directly (one native render, no main-product flicker). If a request can't be
      // matched (e.g. not pre-warmed), it's left untouched and the post-add
      // reconciler swap covers it.
      interceptAdd: true,

      // Resolve the subscription product dynamically from the main product handle.
      autoMatchByHandle: true,
      // Suffix appended to the main handle to form the subscription product handle.
      handleSuffix: '-subscription',

      // OPTIONAL explicit overrides, keyed by MAIN product id (string). Takes
      // precedence over handle matching — leave empty for pure handle resolution.
      // Shape: { [productId]: { mainVariantIds?:[], mainUrl?:'',
      //   tiers: { quarterly:{bundleVariantId,bundleSellingPlanId}, annual:{…} } } }
      rules: {},

      // `bundleMap` is populated by an inline <script> in theme.liquid, keyed by
      // main product handle → { subHandle, variantId, mainUrl, mainVariantIds,
      // plans[] }. Listed here purely as documentation — the defensive merge
      // preserves any pre-existing bundleMap on the config object.
    },

    /**
     * ONE-TIME PACKS (spec 2026-07-07). Three-Month / Six-Month purchase options
     * that add the same hidden `<handle>-subscription` bundle as a ONE-TIME
     * purchase (no selling plan). Six-Month multiplies quantity ×2. The block
     * `blocks/vivana-pack-options.liquid` renders the selector (enabled products
     * only) and sets window.VivanaPackState; the fetch interceptor reads it via
     * window.VivanaPackLogic. Independent of subscriptionBundleSwap.
     */
    oneTimePacks: {
      enabled: true, // master kill-switch for the interceptor branch
    },

    /** DOM selectors (update if Appstle changes its markup). */
    selectors: {
      productForm: 'form[action*="/cart/add"]',
      sellingPlanField:
        '#appstle_selling_plan0, select[name="selling_plan"], input[name="selling_plan"]',
      variantIdInput: 'input[name="id"]',
      quantityInput: 'input[name="quantity"]',
      doiNotice: '[data-vivana-doi-notice]',
    },

    /** Feature flags. */
    features: {
      enabled: true,
      autoAddDoiPacks: true, // add doypacks after a quarterly/annual subscription add
      syncDoiOnQtyChange: true, // keep doypack qty in step with subscription qty
      removeOrphanedDoiPacks: true, // remove doypacks if their subscription line is gone
      hideDoiControlsInCart: true, // hide remove/qty controls on doypack lines (CSS)
      showDoiNoticeOnPDP: true, // show "includes N doypacks" notice on the PDP
      enforceMaxInReconciler: true, // clamp any non-doypack line above maxQuantity
      showQtyLimitMessage: true, // toggle the "contact us" message at the cap
    },
  };

  /**
   * Defensive per-key merge. For every key in `source`:
   *  - If `target` doesn't have the key, take the value from `source`.
   *  - If both sides are plain objects (not arrays), recurse — fills in missing
   *    sub-keys without disturbing anything the inline scripts already wrote
   *    (notably `subscriptionBundleSwap.bundleMap`).
   *  - Otherwise (scalar / array in target), leave `target` as-is. Inline writes
   *    win, which is exactly what merchant-driven overrides need.
   *
   * @param {*} target
   * @param {*} source
   */
  function isPlainObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v);
  }
  function mergeDefaults(target, source) {
    for (var k in source) {
      if (!Object.prototype.hasOwnProperty.call(source, k)) continue;
      if (!(k in target)) {
        target[k] = source[k];
      } else if (isPlainObject(target[k]) && isPlainObject(source[k])) {
        mergeDefaults(target[k], source[k]);
      }
      // else: target has a scalar/array — keep it. Inline value wins.
    }
  }

  window.VIVANA_SUBSCRIPTION_CONFIG = window.VIVANA_SUBSCRIPTION_CONFIG || {};
  mergeDefaults(window.VIVANA_SUBSCRIPTION_CONFIG, defaults);
})();
